import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MdDirections } from 'react-icons/md';
import { SlArrowRight } from "react-icons/sl";
import { AiOutlineMinus, AiOutlinePlus, AiFillStar, AiOutlineStar, AiOutlineShopping, AiOutlineCheckCircle } from 'react-icons/ai';
import { useStateContext } from '../context/StateContext';
import { urlFor } from '../sanity/sanityClient';

const ProductDetail = ({ products }) => {
  const [index, setIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastProduct, setToastProduct] = useState(null);
  const { setShowCart, decQty, incQty, qty, onAdd } = useStateContext();

  const handleAddToCart = (product) => {
    const focusedImage = product.image[index];
    const productWithFocusedImage = { ...product, image: [focusedImage] };
    onAdd(productWithFocusedImage, qty);

    setToastProduct({
      name: product.name,
      quantity: product.quantity || 1
    });
    setShowToast(true);
  };

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastProduct(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const handleBuyNow = (product) => {
    const focusedImage = product.image[index];
    const productWithFocusedImage = { ...product, image: [focusedImage] };
    onAdd(productWithFocusedImage, qty);
    setShowCart(true);
  };

  const handleGetDirections = (productLocation) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatitude = position.coords.latitude;
          const userLongitude = position.coords.longitude;
          const directionsUrl = `https://www.google.com/maps/dir/?api=1&origin=${userLatitude},${userLongitude}&destination=${productLocation.lat},${productLocation.lng}&travelmode=driving`;
          window.open(directionsUrl, "_blank");
        },
        (error) => {
          console.error("Error fetching user location:", error);
          alert("Unable to fetch your location. Please allow location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  return (
    <div className="product-detail-container-two">
      {showToast && (
        <div className="toast-notification">
          <AiOutlineCheckCircle size={24} color="#B8862F" />
          <div>
            <div className="toast-title">Added to Cart</div>
            <div className="toast-message">
              {toastProduct?.quantity} x {toastProduct?.name}
            </div>
          </div>
        </div>
      )}
      
      {products.map((product) => (
        <div key={product._id} className="product-card">
          {/* Action Bar - First Column Above Image */}
          <div className="action-bar">
            {product.geolocation && (
              <button
                className="action-button direction-button"
                onClick={() => handleGetDirections(product.geolocation)}
              >
                <MdDirections size={20} />
                <span>Get Directions</span>
              </button>
            )}
            
            {product.user && product.image?.[0]?.asset?._ref && (
              <Link 
                to={{
                  pathname: `/chat/${product.user}`,
                  state: { productImage: product.image[0].asset._ref }
                }}
                className="action-button message-button"
                onClick={() => {
                  localStorage.setItem('currentProduct', JSON.stringify({
                    image: product.image[0].asset._ref
                  }));
                }}
              >
                <span>Send Message</span>
                <SlArrowRight size={14} />
              </Link>
            )}
          </div>
          
          {/* Main Image Container with Status Badge */}
          <div className="image-container">
            {product.status && (
              <span className={`status-badge ${product.status}`}>
                {product.status.toUpperCase()}
              </span>
            )}
            <img
              src={urlFor(product.image?.[index] || product.image?.[0])}
              alt={product.name}
              className="main-image"
            />
          </div>
          
          {/* Horizontal Scrollable Thumbnails */}
          <div className="thumbnail-scroll">
            {product.image.map((image, i) => (
              <button
                key={i}
                className={`thumbnail-btn ${i === index ? 'active' : ''}`}
                onClick={() => setIndex(i)}
              >
                <img
                  src={urlFor(image)}
                  alt={`${product.name} ${i + 1}`}
                  className="thumbnail-image"
                />
              </button>
            ))}
          </div>
          
          {/* Product Info - Tightly Grouped */}
          <div className="product-info">
            {/* Price - Directly at top */}
            <div className="price-section">
              <span className="currency">{product.currency || 'Tzs'}</span>
              <span className="price">{product.price.toLocaleString()}/=</span>
            </div>
            
            {/* Details - Minimal spacing */}
            <p className="product-details">{product.details}</p>
            
            {/* Rating and Quantity - Side by side */}
            <div className="rating-quantity">
              <div className="rating">
                <AiFillStar className="star-filled" />
                <AiFillStar className="star-filled" />
                <AiFillStar className="star-filled" />
                <AiFillStar className="star-filled" />
                <AiOutlineStar className="star-outline" />
                <span className="rating-count">(4)</span>
              </div>
              
              <div className="quantity-control">
                <button onClick={decQty} className="qty-btn">
                  <AiOutlineMinus />
                </button>
                <span className="qty-value">{qty}</span>
                <button onClick={incQty} className="qty-btn">
                  <AiOutlinePlus />
                </button>
              </div>
            </div>
            
            {/* Action Buttons - Directly below with minimal gap */}
            <div className="product-actions">
              <button 
                className="add-to-cart-btn"
                onClick={() => handleAddToCart(product)}
              >
                <AiOutlineShopping size={18} />
                Add to Cart
              </button>
              <button 
                className="buy-now-btn"
                onClick={() => handleBuyNow(product)}
              >
                Buy Now
              </button>
            </div>
          </div>
        </div>
      ))}
      
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@500;700;800&family=Inter:wght@400;500;600&display=swap');

        .product-detail-container-two {
          /* One coherent palette instead of the mixed blue/green/amber before:
             a single warm gold accent carries emphasis (price, active state,
             rating, primary CTA), ink handles text and the secondary button,
             and status badges keep semantic green/red since those signal
             availability rather than brand. */
          --ink: #1a1a1a;
          --muted: #64748b;
          --line: #f0f0f0;
          --surface: #f8fafc;
          --accent: #B8862F;
          --accent-soft: rgba(184, 134, 47, 0.12);
          --font-heading: 'Manrope', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

          width: 100%;
          margin: 0;
          padding: 0;
          background: white;
          min-height: 90vh;
          font-family: var(--font-body);
          color: var(--ink);
        }

        /* Product Card - Full Width */
        .product-card {
          width: 100%;
          max-width:100vw;
          background:white;
          display: flex;
          flex-direction: column;
        }

        /* Action Bar - First Column */
        .action-bar {
          width: 100%;
          display: flex;
          gap: 8px;
          padding: 8px 12px;
          background: white;
          border-bottom: 1px solid var(--line);
        }

        .action-button {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 6px;
          border: none;
          border-radius: 25px;
          font-family: var(--font-body);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          text-decoration: none;
          background: var(--surface);
          color: var(--ink);
          transition: all 0.2s ease;
        }

        .direction-button {
          color: var(--accent);
        }

        .message-button {
          color: var(--ink);
        }

        /* Image Container - Full Width */
        .image-container {
          width: 100%;
          position: relative;
          background: #f5f5f5;
        }

        /* Main Image - Full Width */
        .main-image {
          width: 100%;
          height: auto;
          aspect-ratio: 1/1;
          object-fit: cover;
          display: block;
        }

        /* Status Badge */
        .status-badge {
          position: absolute;
          top: 10px;
          left: 10px;
          padding: 4px 10px;
          font-family: var(--font-body);
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.03em;
          border-radius: 16px;
          color: white;
          z-index: 2;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
        }

        .status-badge.available {
          background: #16a34a;
        }

        .status-badge.sold {
          background: #dc2626;
        }

        /* Thumbnail Scroll - Compact */
        .thumbnail-scroll {
          width: 100%;
          display: flex;
          gap: 6px;
          padding: 8px 12px;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          background: white;
          border-bottom: 1px solid var(--line);
        }

        .thumbnail-scroll::-webkit-scrollbar {
          height: 2px;
        }

        .thumbnail-btn {
          flex: 0 0 auto;
          border: 2px solid transparent;
          border-radius: 6px;
          overflow: hidden;
          background: none;
          cursor: pointer;
          padding: 0;
          transition: all 0.2s ease;
        }

        .thumbnail-btn.active {
          border-color: var(--accent);
        }

        .thumbnail-image {
          width: 55px;
          height: 55px;
          object-fit: cover;
          border-radius: 4px;
        }

        /* Product Info - Tightly Packed */
        .product-info {
          width: 100%;
          padding: 10px 12px;
        }

        /* Price Section - Compact */
        .price-section {
          margin-bottom: 2px;
        }

        .currency {
          font-family: var(--font-body);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: var(--muted);
          margin-right: 3px;
        }

        .price {
          font-family: var(--font-heading);
          font-size: 25px;
          font-weight: 800;
          color: var(--accent);
          line-height: 1.15;
        }

        /* Product Details - Minimal */
        .product-details {
          font-size: 14px;
          line-height: 1.4;
          color: var(--muted);
          margin: 0 0 6px;
        }

        /* Rating + Quantity - Compact */
        .rating-quantity {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;
          padding-top: 6px;
          border-top: 1px solid var(--line);
        }

        .rating {
          display: flex;
          align-items: center;
          gap: 2px;
        }

        .star-filled {
          color: var(--accent);
          font-size: 16px;
        }

        .star-outline {
          color: #e2e2e2;
          font-size: 16px;
        }

        .rating-count {
          font-size: 12px;
          color: var(--muted);
          margin-left: 4px;
        }

        /* Quantity Control - Compact */
        .quantity-control {
          display: flex;
          align-items: center;
          gap: 8px;
          background: var(--surface);
          padding: 3px 5px;
          border-radius: 25px;
        }

        .qty-btn {
          width: 28px;
          height: 28px;
          border: none;
          background: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: var(--ink);
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
        }

        .qty-btn:active {
          transform: scale(0.9);
        }

        .qty-value {
          font-family: var(--font-heading);
          font-weight: 700;
          color: var(--ink);
          min-width: 20px;
          text-align: center;
          font-size: 15px;
        }

        /* Product Buttons - Compact and Direct */
        .product-actions {
          display: flex;
          gap: 8px;
        }

        .add-to-cart-btn {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 12px 8px;
          border: 1.5px solid var(--ink);
          border-radius: 28px;
          background: white;
          color: var(--ink);
          font-family: var(--font-body);
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .buy-now-btn {
          flex: 1;
          padding: 12px 8px;
          border: none;
          border-radius: 28px;
          background: var(--accent);
          color: white;
          font-family: var(--font-body);
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          box-shadow: 0 3px 10px var(--accent-soft);
          transition: all 0.2s ease;
        }

        .add-to-cart-btn:active,
        .buy-now-btn:active {
          transform: scale(0.97);
        }

        /* Toast */
        .toast-notification {
          position: fixed;
          top: 20%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(26,26,26,0.94);
          backdrop-filter: blur(10px);
          color: white;
          padding: 12px 20px;
          border-radius: 30px;
          display: flex;
          align-items: center;
          gap: 10px;
          z-index: 1000;
          box-shadow: 0 10px 25px rgba(0,0,0,0.2);
          animation: slideDown 0.3s ease;
        }

        .toast-title {
          font-family: var(--font-heading);
          font-weight: 700;
          font-size: 14px;
        }

        .toast-message {
          font-size: 12px;
          opacity: 0.9;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translate(-50%, -60%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetail;
